/* MyStoriesTrigger — Instagram-style avatar button + Quick Actions menu.
   - Wraps the AppShell mobile top-bar avatar.
   - Poll /api/stories/me to know if the user has active stories → renders
     the ORIO gradient ring around the avatar.
   - Tap → opens a bottom-sheet Quick Actions menu with 4 key shortcuts:
       1. Le mie storie (view) OR Pubblica storia (upload) — dinamico
       2. Modifica profilo
       3. Cassetto digitale (documenti sanitari)
       4. Presenza sulla mappa (toggle map_visible)
   - Long-press → fallback diretto al profilo.
*/
import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  BookOpen, Camera, ChevronRight, Eye, Loader2, MapPin, Play,
  Sparkles, UserCog, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";
import StoriesMine from "@/pages/match/StoriesMine";

export default function MyStoriesTrigger() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [hasActive, setHasActive] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const pressTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stories/me");
      setHasActive(!!data.has_active);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const initial = (user?.name || "U").trim().charAt(0).toUpperCase();

  const onTap = () => {
    hapticTap();
    setMenuOpen(true);
  };

  const onPressStart = () => {
    pressTimer.current = setTimeout(() => {
      pressTimer.current = null;
      nav("/app/profilo");
    }, 550);
  };
  const onPressEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  // Handlers wired to the bottom-sheet actions.
  const openStories = () => {
    setMenuOpen(false);
    if (hasActive) setViewerOpen(true);
    else setUploaderOpen(true);
  };
  const goProfile = () => { setMenuOpen(false); nav("/app/profilo"); };
  const goCassetto = () => { setMenuOpen(false); nav("/app/cassetto"); };

  return (
    <>
      <button
        data-testid="home-my-stories-avatar"
        onClick={onTap}
        onTouchStart={onPressStart}
        onTouchEnd={onPressEnd}
        onMouseDown={onPressStart}
        onMouseUp={onPressEnd}
        onMouseLeave={onPressEnd}
        aria-label="Azioni rapide"
        className="relative h-10 w-10 rounded-full flex items-center justify-center"
        style={{
          background: hasActive
            ? "conic-gradient(from 0deg, #FF7A1A, #F4B83C, #FF7A1A, #F4B83C, #FF7A1A)"
            : "transparent",
          padding: hasActive ? "2px" : "0px",
        }}
      >
        <span className="h-full w-full rounded-full overflow-hidden border border-[#F0EBE3] flex items-center justify-center bg-white">
          {user?.photo_url ? (
            <img src={user.photo_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="font-display text-[15px] text-[#FF7A1A] font-semibold">
              {initial}
            </span>
          )}
        </span>
        {/* "•••" badge — hints there's a quick menu behind the avatar */}
        {!hasActive && (
          <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full text-[10px] font-bold text-white flex items-center justify-center border-2 border-white leading-none"
                style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}
                aria-hidden="true">
            +
          </span>
        )}
      </button>

      {/* Bottom-sheet Quick Actions menu */}
      <QuickActionsSheet
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        hasActiveStories={hasActive}
        onOpenStories={openStories}
        onOpenProfile={goProfile}
        onOpenCassetto={goCassetto}
      />

      {viewerOpen && (
        <StoriesMine
          onClose={() => { setViewerOpen(false); load(); }}
          onOpenUploader={() => { setViewerOpen(false); setUploaderOpen(true); }}
        />
      )}
      {uploaderOpen && (
        <StoriesMine
          uploaderOnly
          onClose={() => { setUploaderOpen(false); setRefreshTick((t) => t + 1); }}
        />
      )}
    </>
  );
}

/* ─── Bottom-sheet quick actions menu ─────────────────────────────── */
function QuickActionsSheet({
  open, onClose, hasActiveStories,
  onOpenStories, onOpenProfile, onOpenCassetto,
}) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[1800] flex items-end sm:items-center justify-center"
          data-testid="home-quick-menu"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            className="relative w-full sm:max-w-md mx-3 rounded-t-[28px] sm:rounded-[28px] border border-[#F0EBE3] bg-white overflow-hidden"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grabber */}
            <div className="sm:hidden pt-3 pb-1 flex justify-center">
              <span className="h-1 w-10 rounded-full bg-[#E5DECF]" />
            </div>

            {/* Header */}
            <div className="px-5 pt-2 pb-4 flex items-start gap-3">
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white"
                style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                         boxShadow: "0 10px 22px -8px rgba(255,122,26,0.45)" }}>
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-[#C25612]">Azioni rapide</div>
                <div className="font-display text-xl leading-tight text-[#1A1A1A] mt-0.5">
                  Cosa vuoi fare oggi?
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Chiudi"
                data-testid="quick-menu-close"
                className="h-9 w-9 rounded-full bg-[#F4EFE6] flex items-center justify-center hover:bg-[#EAE3D2]"
              >
                <X className="h-4 w-4 text-[#6B6B6B]" />
              </button>
            </div>

            {/* Action rows */}
            <div className="px-3 pb-1 space-y-1.5">
              <ActionRow
                testid="quick-story"
                icon={hasActiveStories ? Play : Camera}
                iconBg="linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)"
                title={hasActiveStories ? "Le mie storie" : "Pubblica una storia"}
                subtitle={hasActiveStories ? "Guarda e aggiungi una nuova storia" : "Foto o video da condividere in 10 s"}
                onClick={onOpenStories}
              />
              <ActionRow
                testid="quick-profile"
                icon={UserCog}
                iconBg="linear-gradient(135deg, #5B9EE0 0%, #7B61FF 100%)"
                title="Modifica profilo"
                subtitle="Foto, bio, città, interessi"
                onClick={onOpenProfile}
              />
              <ActionRow
                testid="quick-cassetto"
                icon={BookOpen}
                iconBg="linear-gradient(135deg, #34D399 0%, #2DBA60 100%)"
                title="Cassetto digitale"
                subtitle="Vaccinazioni, referti, microchip"
                onClick={onOpenCassetto}
              />
              <PresenceActionRow onDone={onClose} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function ActionRow({ icon: Icon, iconBg, title, subtitle, onClick, testid, trailing }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full text-left px-3 py-3 rounded-2xl flex items-center gap-3 hover:bg-[#FAF7F0] active:bg-[#F4EFE6] transition-colors"
    >
      <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white"
        style={{ background: iconBg, boxShadow: "0 8px 18px -8px rgba(0,0,0,0.20)" }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-[#1A1A1A] leading-tight">{title}</div>
        <div className="text-[12px] text-[#7A7A7A] mt-0.5 leading-snug">{subtitle}</div>
      </div>
      {trailing ?? <ChevronRight className="h-4 w-4 text-[#B5AE9E] shrink-0" />}
    </button>
  );
}

/* PresenceActionRow — toggles /me/visibility (Presenza Live 15km) inline
   from the quick menu, without navigating away. Fetches the current state
   the first time the sheet opens (lazy). */
function PresenceActionRow({ onDone }) {
  const [visible, setVisible] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/me/visibility")
      .then((r) => { if (!cancelled) setVisible(!!r.data?.visible); })
      .catch(() => { if (!cancelled) setVisible(false); });
    return () => { cancelled = true; };
  }, []);

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy || visible === null) return;
    setBusy(true);
    const next = !visible;
    try {
      let payload = { visible: next };
      if (next && navigator.geolocation) {
        try {
          const pos = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000, enableHighAccuracy: false })
          );
          payload = { visible: next, lat: pos.coords.latitude, lng: pos.coords.longitude };
        } catch { /* silent — still send toggle */ }
      }
      await api.put("/me/visibility", payload);
      setVisible(next);
      toast.success(next ? "Sei visibile sulla mappa · 15 km" : "Modalità invisibile attiva");
    } catch {
      toast.error("Errore aggiornamento visibilità");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid="quick-presence"
      className="w-full text-left px-3 py-3 rounded-2xl flex items-center gap-3 hover:bg-[#FAF7F0] active:bg-[#F4EFE6] transition-colors"
    >
      <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white relative"
        style={{
          background: visible
            ? "linear-gradient(135deg, #34D399 0%, #2DBA60 100%)"
            : "linear-gradient(135deg, #B0A084 0%, #8C8168 100%)",
          boxShadow: "0 8px 18px -8px rgba(0,0,0,0.20)",
        }}>
        <MapPin className="h-5 w-5" />
        {visible && (
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#34D399] animate-pulse" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-[#1A1A1A] leading-tight inline-flex items-center gap-2">
          Presenza sulla mappa
          <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
            style={{
              background: visible ? "rgba(45,186,96,0.15)" : "rgba(176,160,132,0.20)",
              color: visible ? "#2DBA60" : "#8C8168",
            }}>
            {visible === null ? "…" : visible ? "ATTIVA" : "OFF"}
          </span>
        </div>
        <div className="text-[12px] text-[#7A7A7A] mt-0.5 leading-snug">
          {visible ? "Appari sulla mappa a 15 km · tocca per nasconderti" : "Tocca per apparire sulla mappa · 15 km"}
        </div>
      </div>
      {busy ? (
        <Loader2 className="h-4 w-4 text-[#B5AE9E] shrink-0 animate-spin" />
      ) : (
        <Eye className="h-4 w-4 text-[#B5AE9E] shrink-0" />
      )}
    </button>
  );
}
